import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_COLORS, STATUS_LABEL, rp } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Inbox } from "lucide-react";

export const PageHeader = ({ eyebrow, title, subtitle, actions, testId }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 fade-up" data-testid={testId}>
    <div>
      {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
      <h1 className="font-display text-3xl md:text-4xl font-bold text-white">{title}</h1>
      {subtitle && <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, sub, accent, testId, icon: Icon }) => (
  <div className={cn("panel p-4 md:p-5 relative overflow-hidden fade-up", accent && "panel-red")} data-testid={testId}>
    {Icon && <Icon className="absolute right-3 top-3 h-5 w-5 text-red-500/70" />}
    <div className="eyebrow">{label}</div>
    <div className={cn("kpi mt-1", accent ? "text-red-400" : "text-white")}>{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

export const StatusBadge = ({ status, testId }) => (
  <Badge data-testid={testId} className={cn("rounded-sm font-mono text-[11px] tracking-wide border-0 hover:opacity-90", STATUS_COLORS[status] || "bg-zinc-700 text-zinc-100")}>
    {STATUS_LABEL[status] || status}
  </Badge>
);

export const Money = ({ v, className }) => <span className={cn("font-mono", className)}>{rp(v)}</span>;

export const EmptyState = ({ text = "Belum ada data", testId }) => (
  <div className="flex flex-col items-center justify-center py-14 text-zinc-500" data-testid={testId}>
    <Inbox className="h-8 w-8 mb-2 text-zinc-700" />
    <p className="text-sm">{text}</p>
  </div>
);

export const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-zinc-400 uppercase tracking-wider">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
  </div>
);

export const TextField = ({ label, value, onChange, type = "text", placeholder, testId, required, hint, ...rest }) => (
  <Field label={label} hint={hint}>
    <Input data-testid={testId} type={type} value={value ?? ""} placeholder={placeholder} required={required}
      onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
      className="bg-zinc-950 border-zinc-800 focus-visible:ring-red-600" {...rest} />
  </Field>
);

export const AreaField = ({ label, value, onChange, testId, placeholder }) => (
  <Field label={label}>
    <Textarea data-testid={testId} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-zinc-950 border-zinc-800 focus-visible:ring-red-600" />
  </Field>
);

export const SelectField = ({ label, value, onChange, options, placeholder = "Pilih…", testId }) => (
  <Field label={label}>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="bg-zinc-950 border-zinc-800 focus:ring-red-600"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-opt-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);

export const Modal = ({ open, onClose, title, children, wide, testId }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={testId} className={cn("bg-[#121217] border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto", wide ? "max-w-3xl" : "max-w-lg")}>
      <DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const Table = ({ cols, rows, render, keyFn, empty, testId }) => (
  <div className="panel overflow-x-auto" data-testid={testId}>
    {rows.length === 0 ? <EmptyState text={empty} /> : (
      <table className="w-full table-dense">
        <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={keyFn ? keyFn(r) : i} data-testid={`${testId}-row-${i}`}>{render(r, i)}</tr>)}</tbody>
      </table>
    )}
  </div>
);

export const useFetch = (fn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = async () => {
    setLoading(true);
    try { setData(await fn()); setErr(null); } catch (e) { setErr(e); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, deps);
  return { data, loading, err, reload: load, setData };
};

export const Spinner = () => (
  <div className="flex items-center justify-center py-16" data-testid="loading-spinner">
    <div className="h-8 w-8 rounded-full border-2 border-zinc-800 border-t-red-600 animate-spin" />
  </div>
);
